import apiClient from './apiClient';
import {
  Shift,
  ShiftInput,
} from '../../domain/myShift';

// ========== リトライ設定 ==========

const MAX_RETRY_COUNT = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // exponential backoff

/**
 * リトライ機能付きAPIコール
 * @param operation - 実行する非同期操作
 * @param onRetrying - リトライ時のコールバック
 * @returns 操作の結果
 */
async function withRetry<T>(
  operation: () => Promise<T>,
  onRetrying?: () => void
): Promise<T> {
  let lastError: Error = new Error('Unknown error');

  for (let attempt = 0; attempt < MAX_RETRY_COUNT; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      if (attempt < MAX_RETRY_COUNT - 1) {
        onRetrying?.();
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
      }
    }
  }

  throw lastError;
}

// ========== Request/Response Types ==========

/**
 * シフト一覧取得レスポンス
 * @endpoint GET /api/my-shifts/{doctor_id}?year_month={year_month}
 */
export interface FetchShiftsResponse {
  /** 成功フラグ */
  success: boolean;
  /** シフト配列 */
  data: Shift[];
  /** メタ情報 */
  meta?: {
    /** 取得期間 */
    period: {
      /** 開始日（YYYY-MM-DD形式） */
      from: string;
      /** 終了日（YYYY-MM-DD形式） */
      to: string;
    };
    /** 総件数 */
    total_count: number;
    /** 月次シフト枠ステータス（draft/fixed_recruiting/fixed_confirmed/recruiting/closed/confirmed） */
    monthly_status?: string | null;
  };
}

/**
 * マイシフト対象日付置換リクエスト
 * @endpoint PUT /api/my-shifts/{doctor_id}/{year_month}
 */
export interface UpdateShiftsParams {
  /** 置換対象の日付（YYYY-MM-DD形式）。この日付以外には影響しない */
  targetDates: string[];
  /** クライアントが想定する月次ステータス（fixed_recruiting / recruiting）。不一致は409 */
  expectedStatus: string;
  /** 送信するシフト配列（対象日付の最終状態。空配列可） */
  shifts: ShiftInput[];
}

/**
 * マイシフト対象日付置換レスポンス
 * @endpoint PUT /api/my-shifts/{doctor_id}/{year_month}
 */
export interface UpdateShiftsResponse {
  /** 成功フラグ */
  success: boolean;
  /** 更新されたシフト配列 */
  data: Shift[];
}

/**
 * マイシフトAPIエラー
 * @description バックエンドの error.code を保持する（STATUS_CONFLICT / VALIDATION_ERROR / NOT_FOUND 等）
 */
export class MyShiftApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'MyShiftApiError';
  }
}

// ========== API Functions ==========

/**
 * 指定年月のシフト一覧を取得
 * @param doctorId - 医師ID
 * @param yearMonth - 対象年月（YYYY-MM形式）
 * @param onRetrying - リトライ時のコールバック
 * @returns シフト一覧
 * @endpoint GET /api/my-shifts/{doctor_id}?year_month={year_month}
 */
export const fetchShifts = async (
  doctorId: number,
  yearMonth: string,
  onRetrying?: () => void
): Promise<FetchShiftsResponse> => {
  return withRetry(async () => {
    const response = await apiClient.get<FetchShiftsResponse>(
      `/api/my-shifts/${doctorId}`,
      {
        params: { year_month: yearMonth },
      }
    );
    return response.data;
  }, onRetrying);
};

/**
 * マイシフトを対象日付置換で更新
 *
 * targetDates で指定した日付の編集可能なシフトのみが送信内容で置き換えられる。
 * 「その日の全取消」は targetDates に日付を含めて shifts を空にする。
 *
 * @param doctorId - 医師ID
 * @param yearMonth - 対象年月（YYYY-MM形式）
 * @param params - 置換パラメータ（targetDates / expectedStatus / shifts）
 * @returns 更新されたシフト一覧
 * @throws MyShiftApiError STATUS_CONFLICT（管理者のフェーズ遷移と競合）等
 * @endpoint PUT /api/my-shifts/{doctor_id}/{year_month}
 */
export const updateShifts = async (
  doctorId: number,
  yearMonth: string,
  params: UpdateShiftsParams
): Promise<UpdateShiftsResponse> => {
  try {
    const response = await apiClient.put<UpdateShiftsResponse>(
      `/api/my-shifts/${doctorId}/${yearMonth}`,
      {
        target_dates: params.targetDates,
        expected_status: params.expectedStatus,
        shifts: params.shifts,
      }
    );
    return response.data;
  } catch (error: unknown) {
    const apiError = extractApiError(error);
    if (apiError) {
      throw apiError;
    }
    throw error;
  }
};

/**
 * axiosエラーからバックエンドの error.code / message を取り出す
 */
const extractApiError = (error: unknown): MyShiftApiError | null => {
  const response = (error as { response?: { data?: { error?: { code?: string; message?: string } } } })
    ?.response;
  const apiError = response?.data?.error;
  if (apiError?.code) {
    return new MyShiftApiError(apiError.code, apiError.message ?? 'シフトの保存に失敗しました');
  }
  return null;
};

// myShiftApi オブジェクトとしてもエクスポート
export const myShiftApi = {
  fetchShifts,
  updateShifts,
};

export default myShiftApi;
